import { NativePurchases, PURCHASE_TYPE } from "@capgo/native-purchases";
import { Capacitor } from "@capacitor/core";
import { v5 as uuidv5 } from "uuid";

// A fixed application namespace so a given user id always maps to the same
// appAccountToken (an obfuscated account identifier the stores use for fraud
// detection and account linking). Generated once; must never change.
const APP_NAMESPACE = "6ba7b810-9dad-11d1-80b4-00c04fd430c8";

const PLATFORM_GOOGLE_PLAY = "google_play";
const PLATFORM_APP_STORE = "app_store";

/**
 * Thin wrapper around @capgo/native-purchases.
 *
 * Runs the native store purchase flow and normalizes the result into the shape
 * the backend expects (purchase_token on Android, receipt on iOS). Store
 * purchases are only available on native platforms; on web everything reports
 * unsupported so callers can fall back to a display-only price.
 *
 * Mirrors the static-class shape of PushManager.
 */
export default class IapManager
{
    static isSupported = false;

    static platform = null;

    /**
     * Detect the platform and whether billing is available. Safe to call on
     * web (reports unsupported) and safe to call more than once.
     */
    static async initialize()
    {
        this.platform = Capacitor.getPlatform();

        if (! Capacitor.isNativePlatform()) {
            this.isSupported = false;
            return;
        }

        try {
            const { isBillingSupported } = await NativePurchases.isBillingSupported();
            this.isSupported = isBillingSupported === true;
        } catch {
            this.isSupported = false;
        }
    }

    /**
     * The backend platform key for the current device, matching
     * IapService::PLATFORM_* on the server.
     */
    static platformKey()
    {
        return this.platform === "android" ? PLATFORM_GOOGLE_PLAY : PLATFORM_APP_STORE;
    }

    /**
     * A stable, obfuscated per-user token (UUID v5) for the store's
     * appAccountToken / ObfuscatedAccountId. Never send raw PII here.
     */
    static generateAppAccountToken(userId)
    {
        return uuidv5(String(userId), APP_NAMESPACE);
    }

    /**
     * Fetch live product metadata (localized priceString, etc.) for the given
     * store identifiers. Returns [] when store purchases are unsupported.
     */
    static async getProducts(identifiers, type = PURCHASE_TYPE.INAPP)
    {
        if (! this.isSupported || ! Array.isArray(identifiers) || identifiers.length === 0) {
            return [];
        }

        try {
            const { products } = await NativePurchases.getProducts({
                productIdentifiers: identifiers,
                productType: type,
            });
            return products || [];
        } catch {
            return [];
        }
    }

    /**
     * Run the native purchase flow for a consumable (chest) product and return
     * the proof the backend needs to validate.
     */
    static async purchaseConsumable(productIdentifier, userId)
    {
        const transaction = await NativePurchases.purchaseProduct({
            productIdentifier,
            productType: PURCHASE_TYPE.INAPP,
            quantity: 1,
            appAccountToken: this.generateAppAccountToken(userId),
            isConsumable: true,
        });

        return this.normalizeTransaction(transaction);
    }

    /**
     * Run the native purchase flow for an auto-renewable subscription. On
     * Android a planIdentifier (base plan) is required.
     */
    static async purchaseSubscription(productIdentifier, planIdentifier, userId)
    {
        const transaction = await NativePurchases.purchaseProduct({
            productIdentifier,
            planIdentifier,
            productType: PURCHASE_TYPE.SUBS,
            quantity: 1,
            appAccountToken: this.generateAppAccountToken(userId),
        });

        return this.normalizeTransaction(transaction);
    }

    /**
     * Restore previous purchases (required by Apple for review). No-op on web.
     */
    static async restorePurchases()
    {
        if (! this.isSupported) {
            return;
        }
        await NativePurchases.restorePurchases();
    }

    /**
     * Open the native "manage subscriptions" surface. No-op on web.
     */
    static async manageSubscriptions()
    {
        if (! this.isSupported || typeof NativePurchases.manageSubscriptions !== "function") {
            return;
        }
        await NativePurchases.manageSubscriptions();
    }

    /**
     * Shape a native Transaction into the backend contract: transactionId plus
     * the platform proof (purchase_token on Android, receipt on iOS) and the
     * platform key.
     */
    static normalizeTransaction(transaction)
    {
        return {
            transactionId: transaction.transactionId,
            purchaseToken: transaction.purchaseToken || null, // Android
            receipt: transaction.receipt || null,             // iOS
            platform: this.platformKey(),
        };
    }
}
